/* eslint-disable @typescript-eslint/restrict-plus-operands */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as d3 from 'd3';
import { Box, useTheme } from '@mui/material';
import { ErrorTimelinePoint, HardwareErrorsResponse } from '../../types/hardwareErrors';

export interface ErrorsTimelineProps {
    errorsData: HardwareErrorsResponse | undefined;
    onDataPointClick?: (dataPoint: ErrorTimelinePoint) => void;
    onBrushChange?: (range: [number, number] | null) => void;
    fillHeight?: boolean;
}

/**
 * Computes the bucket size in milliseconds so that the chart fills with approximately
 * floor(chartWidth / 6) bars — each bar 5 px wide + 1 px gap.
 * Falls back to a 1-day bucket before the container has been measured.
 */
const computeBucketMs = (errors: HardwareErrorsResponse | undefined, chartWidth: number): number => {
    if (!errors?.errors || errors.errors.length === 0) return 86_400_000;
    const ts = errors.errors.map(e => new Date(e.errorDate).getTime());
    const spanMs = Math.max(...ts) - Math.min(...ts);
    if (spanMs <= 0) return 60_000; // single timestamp — arbitrary 1-minute bucket
    const numBars = chartWidth > 0 ? Math.max(1, Math.floor(chartWidth / 6)) : 200;
    return spanMs / numBars;
};

/** Human-readable bucket duration shown below the X-axis (e.g. "~5s / bar", "~2h / bar"). */
const bucketLabel = (ms: number): string => {
    if (ms <        1_000) return `~${Math.round(ms)}ms / bar`;
    if (ms <       60_000) return `~${Math.round(ms / 1_000)}s / bar`;
    if (ms <    3_600_000) return `~${Math.round(ms / 60_000)}min / bar`;
    if (ms <   86_400_000) return `~${Math.round(ms / 3_600_000)}h / bar`;
    if (ms <  604_800_000) return `~${Math.round(ms / 86_400_000)}d / bar`;
    return `~${Math.round(ms / 604_800_000)}w / bar`;
};

/** D3 time-format string appropriate for the given bucket size. */
const bucketTickFormat = (ms: number): string => {
    if (ms <    60_000) return '%H:%M:%S'; // sub-minute
    if (ms < 3_600_000) return '%H:%M';    // sub-hour
    if (ms < 86_400_000) return '%b %d %H:%M'; // sub-day
    if (ms < 604_800_000) return '%b %d';  // sub-week
    return "%b '%y";
};

// ─── Histogram data model ────────────────────────────────────────────────────

interface HistogramSegment {
    errorCode: string;
    source: string;
    description: string | null;
    count: number;
    /** cumulative stack bottom */
    y0: number;
    /** cumulative stack top */
    y1: number;
}

interface HistogramBar {
    date: Date;
    dateKey: string;
    totalCount: number;
    /** Sorted ascending by count — smallest at bottom, largest at visual top */
    segments: HistogramSegment[];
}

/** Numeric prefix of an error code (leading digits), used for source ordering. */
const getErrorPrefix = (errorCode: string): number => {
    const m = errorCode.match(/^(\d+)/);
    return m ? parseInt(m[1], 10) : 9999;
};

/**
 * Stable color palette keyed by ErrorSource — grouped by software role / architectural layer.
 * Sources from SELECT DISTINCT [ErrorSource] FROM [dbo].[REF_ERRORS_SOURCES] (EventsManagerConnections).
 *
 * Color families:
 *   RED       — core hardware & CNC motion control (CNUM, Inverter, Hauser, Laser Rofin)
 *   DARK TEAL — CMachine group: device bus + all devices it orchestrates
 *               (CMachine, ManufacturingDeviceManager, Transfer table conveyor, Spreader OPW, Spc Device)
 *   BLUE      — application machine software / Pilot group (CamKernel, Pilot, Cutting Strategy)
 *   BROWN     — Versalis leather domain, incl. DataManager reporting suite
 *   TEAL/CYAN — scanning & vision systems (Scanner variants, ScannerSPDevice)
 *   GREEN     — cutting production flow (PostPrint, Offload)
 *   SLATE     — transverse shared services (Bibliobus, Flexlm)
 *   DK SLATE  — graphical libraries (DaoLib, GenLib)
 *   COLD BLUE — cooling hardware (KKT Chiller)
 */
const SOURCE_COLORS: Record<string, string> = {
    // ── 1. Core hardware & motion control ── (red family) ──────────────────
    'CNUM':                       '#b71c1c', // prefix  1 — CNC motion controller (hardware core)
    'Inverter':                   '#c62828', // prefix  9 — motor drive / inverter hardware
    'Hauser':                     '#d32f2f', // prefix 36 — Hauser external motion controller
    'Laser Source Rofin':         '#bf360c', // prefix 33 — Rofin laser source (energy hardware)

    // ── 2. CMachine group: device orchestration bus + orchestrated peripherals ── (dark teal family) ──
    // CMachine drives every machine device via RPC/NATS; ManufacturingDeviceManager, conveyors and
    // spreaders are all managed at device-driver level by CMachine.
    'CMachine':                   '#006064', // prefix  2 — C++ device bus; orchestrates all hardware via RPC/NATS
    'ManufacturingDeviceManager': '#004d5e', // prefix 38 — manufacturing device orchestrator (CMachine tier)
    'Transfer table conveyor':    '#005b6b', // prefix 32 — conveyor / transfer table hardware
    'Spreader OPW':               '#006272', // prefix 34 — OPW material spreader device
    'Spc Device':                 '#006b6b', // prefix 41 — SPC peripheral device

    // ── 3. Application machine software / Pilot group ── (blue family) ─────
    // CamKernel is the embedded camera/vision kernel, a subsystem invoked by Pilot.
    'CamKernel':                  '#0d47a1', // prefix  6 — embedded camera/vision processing kernel (Pilot subsystem)
    'Pilot':                      '#1565c0', // prefix  3 — main on-machine orchestrator
    'Cutting Strategy':           '#1976d2', // prefix  7 — cut-path optimisation, orchestrated by Pilot

    // ── 4. Versalis leather domain (Versalis only) ── (warm brown family) ──
    // DataManager is a Versalis-specific data management & reporting suite.
    'LeatherMachineController':   '#3e2723', // prefix 14 — Versalis low-level machine controller (= CMachine on Versalis)
    'LeatherSupervisor':          '#4e342e', // prefix 10 — Versalis process orchestrator (= Pilot on Versalis)
    'LeatherNest':                '#5d4037', // prefix 11 — leather hide nesting (marker placement)
    'LeatherScan':                '#6d4c41', // prefix 12 — leather hide scanner software
    'Hide Locator':               '#795548', // prefix 30 — raw hide shape & position detection
    'DataManager':                '#8d6e63', // prefix 27 — Versalis data management & reporting suite

    // ── 5. Scanning & vision systems ── (teal/cyan family) ─────────────────
    'Scanner':                    '#004d40', // prefix  8 — generic scanner interface
    'Scanner Type 1':             '#00695c', // prefix 28 — external scanner device type 1
    'Scanner Type 2':             '#00838f', // prefix 29 — external scanner device type 2
    'Scanner OPW':                '#0097a7', // prefix 35 — OPW-brand scanner device
    'ScannerSPDevice':            '#26c6da', // prefix 40 — SP scanner device

    // ── 6. Cutting production flow ── (green family) ────────────────────────
    'PostPrint':                  '#2e7d32', // prefix  5 — post-cut label / print output
    'Offload':                    '#33691e', // prefix 13 — cut pieces offloading

    // ── 7. Transverse shared services ── (slate family) ─────────────────────
    // Cross-cutting concerns used by multiple software layers.
    'Bibliobus':                  '#455a64', // prefix  4 — internal work queue / resource manager
    'Flexlm':                     '#4a6572', // prefix 17 — FlexLM software licensing manager

    // ── 8. Graphical libraries ── (dark slate / charcoal family) ────────────
    'DaoLib':                     '#37474f', // prefix 15 — data access / graphics library
    'GenLib':                     '#546e7a', // prefix 16 — general graphics utility library

    // ── 9. Cooling hardware ── (cold blue — single entry) ───────────────────
    'KKT Chiller':                '#0277bd', // prefix 37 — KKT cooling / chiller unit

    'UNKNOWN':                    '#9e9e9e',
};

/**
 * Groups raw errors into bars of exactly `bucketMs` milliseconds.
 * Bucket start = floor(timestamp / bucketMs) × bucketMs — no calendar snapping.
 */
const mapErrorsToHistogram = (errors: HardwareErrorsResponse | undefined, bucketMs: number): HistogramBar[] => {
    if (!errors?.errors || errors.errors.length === 0 || bucketMs <= 0) return [];

    // dateKey → (errorCode\x00source → accumulator)
    const dateGroups: Record<string, Record<string, { count: number; source: string; description: string | null }>> = {};

    errors.errors.forEach(error => {
        const ts = new Date(error.errorDate).getTime();
        const dateKey = new Date(Math.floor(ts / bucketMs) * bucketMs).toISOString();

        const segKey = `${error.errorCode}\x00${error.errorSource ?? 'UNKNOWN'}`;
        if (!dateGroups[dateKey]) dateGroups[dateKey] = {};
        if (!dateGroups[dateKey][segKey]) {
            dateGroups[dateKey][segKey] = { count: 0, source: error.errorSource ?? 'UNKNOWN', description: null };
        }
        dateGroups[dateKey][segKey].count++;
        if (!dateGroups[dateKey][segKey].description && error.errorDescription) {
            dateGroups[dateKey][segKey].description = error.errorDescription;
        }
    });

    return Object.entries(dateGroups)
        .map(([dateKey, segsMap]) => {
            // Parse flat segment list
            const allSegs = Object.entries(segsMap).map(([segKey, d]) => {
                const sep = segKey.indexOf('\x00');
                return { errorCode: segKey.slice(0, sep), source: segKey.slice(sep + 1), description: d.description, count: d.count };
            });

            // Group by source, sort each group by count ascending (smallest at bottom, largest at top)
            const bySource: Record<string, typeof allSegs> = {};
            allSegs.forEach(s => {
                if (!bySource[s.source]) bySource[s.source] = [];
                bySource[s.source].push(s);
            });

            // Order source groups by the error prefix of their error codes (hardware layer first → software layers on top)
            const sourceGroups = Object.entries(bySource)
                .map(([, segs]) => ({
                    prefix: getErrorPrefix(segs[0]?.errorCode ?? ''),
                    segs: segs.sort((a, b) => a.count - b.count),
                }))
                .sort((a, b) => a.prefix - b.prefix);

            // Flatten with stacking offsets
            let cumulative = 0;
            const segments: HistogramSegment[] = [];
            sourceGroups.forEach(group => {
                group.segs.forEach(seg => {
                    const y0 = cumulative;
                    const y1 = cumulative + seg.count;
                    cumulative = y1;
                    segments.push({ ...seg, y0, y1 });
                });
            });

            return { date: new Date(dateKey), dateKey, totalCount: cumulative, segments };
        })
        .sort((a, b) => a.date.getTime() - b.date.getTime());
};

export const ErrorsTimeline: React.FC<ErrorsTimelineProps> = ({ errorsData, onDataPointClick, onBrushChange, fillHeight }) => {
    const svgRef = useRef<SVGSVGElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState<number>(0);
    const [containerHeight, setContainerHeight] = useState<number>(0);
    const theme = useTheme();

    const updateDimensions = useCallback(() => {
        if (containerRef.current) {
            const w = containerRef.current.clientWidth;
            const h = containerRef.current.clientHeight;
            if (w > 0) setContainerWidth(w);
            if (h > 0) setContainerHeight(h);
        }
    }, []);

    useEffect(() => {
        updateDimensions();
        const ro = new ResizeObserver(updateDimensions);
        if (containerRef.current) ro.observe(containerRef.current);
        return () => ro.disconnect();
    }, [updateDimensions]);

    // Bucket size: floor((containerWidth - margins) / 6) bars, each 5 px wide + 1 px gap
    const bucketMs = computeBucketMs(errorsData, Math.max(0, containerWidth - 200));
    const histBars = mapErrorsToHistogram(errorsData, bucketMs);

    useEffect(() => {
        if (!svgRef.current || containerWidth === 0 || histBars.length === 0) return;

        d3.select(svgRef.current).selectAll('*').remove();

        const margin = { top: 20, right: 120, bottom: 50, left: 60 };
        const width  = containerWidth - margin.left - margin.right;
        const svgTotalHeight = fillHeight && containerHeight > 100 ? containerHeight : 400;
        const height = svgTotalHeight - margin.top - margin.bottom;

        // Unique sources from rendered bars, alphabetically (legend order)
        const uniqueSources: string[] = histBars
            .reduce<string[]>((acc, b) => acc.concat(b.segments.map((s: HistogramSegment) => s.source)), [])
            .filter((v, i, a) => a.indexOf(v) === i)
            .sort((a, b) => a.localeCompare(b));

        // X domain: first → last bucket start
        const xDomain: [Date, Date] = histBars.length > 1
            ? [histBars[0].date, histBars[histBars.length - 1].date]
            : [new Date(histBars[0].date.getTime() - bucketMs / 2),
               new Date(histBars[0].date.getTime() + bucketMs / 2)];

        const barWidth = 5;
        const xRange: [number, number] = histBars.length > 1
            ? [barWidth / 2, width - barWidth / 2]
            : [0, width];
        const xScale = d3.scaleTime().domain(xDomain).range(xRange);

        const maxCount = d3.max(histBars, b => b.totalCount) ?? 1;
        const yScale = d3.scaleLinear().domain([0, maxCount]).nice().range([height, 0]);

        const colorScale = (source: string): string => SOURCE_COLORS[source] ?? '#9e9e9e';

        const svg = d3.select(svgRef.current)
            .attr('width',  width  + margin.left + margin.right)
            .attr('height', height + margin.top  + margin.bottom)
            .append('g')
            .attr('transform', `translate(${margin.left},${margin.top})`);

        // ── X axis ──────────────────────────────────────────────────────────
        const maxTicks = Math.max(2, Math.floor(width / 60));
        svg.append('g')
            .attr('transform', `translate(0,${height})`)
            .call(d3.axisBottom(xScale)
                .ticks(Math.min(maxTicks, histBars.length))
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                .tickFormat(d3.timeFormat(bucketTickFormat(bucketMs)) as any))
            .selectAll('text')
            .attr('transform', 'rotate(-45)')
            .style('text-anchor', 'end')
            .style('fill', theme.palette.text.primary);
        svg.selectAll('.domain, .tick line').style('stroke', theme.palette.text.secondary);

        // ── Y axis ──────────────────────────────────────────────────────────
        svg.append('g')
            .call(d3.axisLeft(yScale).ticks(Math.min(10, maxCount)).tickFormat(d3.format('d')))
            .selectAll('text')
            .style('fill', theme.palette.text.primary);

        // ── Axis labels ─────────────────────────────────────────────────────
        svg.append('text')
            .attr('x', width / 2).attr('y', height + margin.bottom - 5)
            .style('text-anchor', 'middle').style('font-size', '11px')
            .style('fill', theme.palette.text.secondary)
            .text(bucketLabel(bucketMs));

        svg.append('text')
            .attr('transform', 'rotate(-90)')
            .attr('y', 0 - margin.left).attr('x', 0 - height / 2).attr('dy', '1em')
            .style('text-anchor', 'middle').style('fill', theme.palette.text.primary)
            .text('Error Count');

        // ── Brush ────────────────────────────────────────────────────────────
        const brush = d3.brushX()
            .extent([[0, 0], [width, height]])
            .on('end', (event: d3.D3BrushEvent<unknown>) => {
                const { selection } = event;
                if (!selection) { if (onBrushChange) onBrushChange(null); return; }
                const [x0, x1] = (selection as [number, number]).map((px: number) => xScale.invert(px).getTime());
                if (onBrushChange) onBrushChange([x0, x1]);
            });
        const brushGroup = svg.append('g').attr('class', 'brush').call(brush);
        brushGroup.select('.selection')
            .style('fill', theme.palette.primary.main).style('fill-opacity', '0.15')
            .style('stroke', theme.palette.primary.main).style('stroke-width', '1px');

        // ── Stacked bars ─────────────────────────────────────────────────────
        histBars.forEach(bar => {
            const xPos = histBars.length > 1
                ? xScale(bar.date) - barWidth / 2
                : (width - barWidth) / 2;

            const sourceCounts: Record<string, number> = {};
            bar.segments.forEach(s => { sourceCounts[s.source] = (sourceCounts[s.source] ?? 0) + 1; });
            const sourceIdx: Record<string, number> = {};

            bar.segments.forEach(seg => {
                sourceIdx[seg.source] = sourceIdx[seg.source] ?? 0;
                const idx   = sourceIdx[seg.source]++;
                const total = sourceCounts[seg.source];

                let fillColor: string;
                if (total <= 1) {
                    fillColor = colorScale(seg.source);
                } else {
                    const hsl = d3.hsl(colorScale(seg.source));
                    const ratio = idx / (total - 1);
                    hsl.l = Math.max(0.18, Math.min(0.82, hsl.l + (ratio - 0.5) * 0.36));
                    fillColor = hsl.formatHex();
                }

                const yTop = yScale(seg.y1);
                const yBot = yScale(seg.y0);
                const segH = Math.max(0, yBot - yTop);

                svg.append('rect')
                    .attr('x', xPos).attr('y', yTop)
                    .attr('width', barWidth).attr('height', segH)
                    .attr('fill', fillColor).attr('stroke', 'none')
                    .style('cursor', 'pointer')
                    .on('mouseover', function(event: any) {
                        d3.select(this).attr('stroke', '#fff').attr('stroke-width', 1).attr('opacity', '0.82');
                        d3.select('body').append('div')
                            .attr('class', 'timeline-tooltip')
                            .style('position', 'fixed')
                            .style('background', 'rgba(20,20,20,0.92)').style('color', '#fff')
                            .style('padding', '8px 10px').style('border-radius', '4px')
                            .style('border-left', `3px solid ${colorScale(seg.source)}`)
                            .style('pointer-events', 'none').style('z-index', '2000')
                            .style('font-size', '12px').style('max-width', '260px')
                            .style('left', `${event.clientX + 14}px`).style('top', `${event.clientY - 10}px`)
                            .html(`
                                <strong>${seg.errorCode}</strong>&nbsp;<span style="opacity:0.65;font-size:11px">[${seg.source}]</span><br/>
                                Count:&nbsp;<strong>${seg.count}</strong>&nbsp;<em style="opacity:0.65;font-size:11px">(${bar.date.toLocaleString()})</em>
                                ${seg.description ? `<hr style="margin:4px 0;border:none;border-top:1px solid rgba(255,255,255,0.2);"/><span style="opacity:0.8;font-size:11px">${seg.description}</span>` : ''}
                            `);
                    })
                    .on('mousemove', function(event: any) {
                        d3.select('.timeline-tooltip')
                            .style('left', `${event.clientX + 14}px`).style('top', `${event.clientY - 10}px`);
                    })
                    .on('mouseout', function() {
                        d3.select(this).attr('stroke', 'none').attr('opacity', '1');
                        d3.selectAll('.timeline-tooltip').remove();
                    })
                    .on('click', function() {
                        d3.selectAll('.timeline-tooltip').remove();
                        if (onDataPointClick) {
                            onDataPointClick({
                                date: bar.dateKey,
                                count: bar.totalCount,
                                source: seg.source,
                                errorCodes: bar.segments.map(s => ({ code: s.errorCode, count: s.count })),
                            });
                        }
                    });
            });
        });

        // ── Legend ───────────────────────────────────────────────────────────
        const legend = svg.append('g').attr('transform', `translate(${width + 10}, 0)`);
        uniqueSources.forEach((source, i) => {
            const row = legend.append('g').attr('transform', `translate(0, ${i * 20})`);
            row.append('rect').attr('width', 15).attr('height', 15).attr('fill', colorScale(source));
            row.append('text').attr('x', 20).attr('y', 12)
                .style('font-size', '12px').style('fill', theme.palette.text.primary).text(source);
        });

    }, [histBars, bucketMs, containerWidth, containerHeight, onDataPointClick, onBrushChange, theme, fillHeight]);

    return (
        <Box ref={containerRef} sx={{ width: '100%', height: '100%', overflow: 'auto' }}>
            <svg ref={svgRef} style={{ width: '100%', display: 'block' }}></svg>
        </Box>
    );
};

export default ErrorsTimeline;
